import type { ButtonProps } from '@wartek-id/button-v1'

import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { LangkahDokumenPersiapanCard } from '../components/Beranda/HubCards/LangkahDokumenPersiapanCard'
import { LangkahDokumenRefleksiTindakLanjutCard } from '../components/Beranda/HubCards/LangkahDokumenRefleksiTindakLanjutCard'
import { LangkahDokumenTindakLanjutCard } from '../components/Beranda/HubCards/LangkahDokumenTindakLanjutCard'
import { LangkahLakukanTindakLanjutCard } from '../components/Beranda/HubCards/LangkahLakukanTindakLanjutCard'
import { LangkahRincianObservasiCard } from '../components/Beranda/HubCards/LangkahRincianObservasiCard'
import { type LangkahObservasiCardProps } from '../components/LangkahObservasiCard'

import type { UrlObject } from 'url'
import type {
  ActivityType,
  ScreenName,
  SKPPelaksanaanTrackerParams,
} from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import type { ReactNode } from 'react'

export type ProgressPercentageStateType = 'unlocked' | 'locked' | 'done'

export type TahapanPelaksanaanType = {
  bagian1: LangkahObservasi[]
  bagian2: LangkahObservasi[]
}

interface EventTrackerDefinition {
  activityType: ActivityType
  screenName: ScreenName
  attributes?: Record<string, any>
  eventTrigger?: SKPPelaksanaanTrackerParams['event_trigger']
}

interface CtaButtonProps extends ButtonProps {
  children: ReactNode
}

export type LangkahObservasi = {
  imageSrc: string
  callToAction: string
  description: string
  CtaButtonProps?: {
    unlocked?: CtaButtonProps
    locked?: CtaButtonProps
    done?: CtaButtonProps
    draft?: CtaButtonProps
    event?: EventTrackerDefinition
  }
  url: UrlObject | string
  progressPercentageState?: {
    0: ProgressPercentageStateType
    25: ProgressPercentageStateType
    50: ProgressPercentageStateType
    75: ProgressPercentageStateType
    100: ProgressPercentageStateType
  }
  CardComponent: (props: LangkahObservasiCardProps) => JSX.Element
  editCtaText?: ReactNode
  lockedText?: string
}

const bagian1: LangkahObservasi[] = [
  {
    imageSrc: '/illustrations/img-document-penci-fill.png',
    callToAction: 'Pemantauan Kinerja: <br /> Isi Dokumen Persiapan',
    description:
      'Diskusi dengan atasan untuk mengisi target perilaku, upaya mempelajari, dan jadwal.',
    editCtaText:
      process.env.NEXT_PUBLIC_SKP_EDIT_FORM_ENABLED === 'true' &&
      'Ubah Target Perilaku',
    CtaButtonProps: {
      unlocked: {
        children: (
          <>
            Isi dokumen
            <span className="sr-only">Persiapan</span>
          </>
        ),
        color: 'blue',
      },
      draft: {
        children: (
          <>
            Lanjutkan
            <span className="sr-only">Dokumen Persiapan</span>
          </>
        ),
        color: 'blue',
      },
      event: {
        activityType: 'pelaksanaan_click_form_a',
        screenName: 'pelaksanaan_hub',
        attributes: {
          section: 'pelaksanaan_hub',
        },
      },
    },
    url: `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/dokumen-persiapan`,
    CardComponent: LangkahDokumenPersiapanCard,
  },
  {
    imageSrc: '/illustrations/img-observation-class-check.png',
    callToAction: 'Pemantauan Kinerja: <br />Pelaksanaan Observasi oleh atasan',
    description: 'Atasan akan mengisi Rubrik Observasi sesuai jadwal',
    lockedText: 'Dilakukan setelah isi Dokumen Persiapan.',
    CtaButtonProps: {
      locked: {
        children: (
          <>
            Cek rincian
            <span className="sr-only">Observasi</span>
          </>
        ),
        color: 'blue',
        disabled: true,
      },
      unlocked: {
        children: (
          <>
            Cek rincian
            <span className="sr-only">Observasi</span>
          </>
        ),
        color: 'blue',
      },
      done: {
        children: (
          <>
            Cek rincian
            <span className="sr-only">Observasi</span>
          </>
        ),
        color: 'white',
      },
      event: {
        activityType: 'pelaksanaan_click_form_b',
        screenName: 'pelaksanaan_hub',
        attributes: {
          section: 'pelaksanaan_observasi',
        },
      },
    },
    url: `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/rincian-observasi`,
    CardComponent: LangkahRincianObservasiCard,
  },
  {
    imageSrc: '/illustrations/img-document-interaction-discussion.png',
    callToAction: 'Pembinaan Kinerja:<br />Isi dokumen tindak lanjut',
    description:
      'Refleksi dan diskusi dengan atasan untuk mengisi dukungan yang dibutuhkan.',
    editCtaText: process.env.NEXT_PUBLIC_SKP_EDIT_FORM_ENABLED === 'true' && (
      <>
        Ubah
        <span className="sr-only">Dokumen Tindak Lanjut</span>
      </>
    ),
    lockedText: 'Menunggu Rating Observasi dari atasan.',
    CtaButtonProps: {
      locked: {
        children: (
          <>
            Isi dokumen
            <span className="sr-only">Dokumen Tindak Lanjut</span>
          </>
        ),
        color: 'blue',
        disabled: true,
      },
      unlocked: {
        children: (
          <>
            Isi dokumen
            <span className="sr-only">Dokumen Tindak Lanjut</span>
          </>
        ),
        color: 'blue',
      },
      done: {
        children: (
          <>
            Cek rincian
            <span className="sr-only">Dokumen Tindak Lanjut</span>
          </>
        ),
        color: 'white',
      },
      draft: {
        children: (
          <>
            Lanjutkan
            <span className="sr-only">Dokumen Tindak Lanjut</span>
          </>
        ),
        color: 'blue',
      },
      event: {
        activityType: 'pelaksanaan_click_form_c',
        screenName: 'pelaksanaan_hub',
        attributes: {
          section: 'pelaksanaan_observasi',
        },
      },
    },
    url: `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/dokumen-tindak-lanjut`,
    CardComponent: LangkahDokumenTindakLanjutCard,
  },
]

const bagian2: LangkahObservasi[] = [
  {
    imageSrc: '/illustrations/img-smartphone-pop-up-modal-cart-section.png',
    callToAction: 'Pembinaan Kinerja: <br />Pelaksanaan Tindak Lanjut',
    description: 'Laksanakan Pilihan Belajar dan tindak lanjut lainnya.',
    lockedText: 'Dilakukan setelah isi Dokumen Tindak Lanjut.',
    url: '',
    CardComponent: LangkahLakukanTindakLanjutCard,
  },
  {
    imageSrc: '/illustrations/img-document-progress-bar-check.png',
    callToAction: 'Pembinaan Kinerja: <br />Isi Dokumen Refleksi Tindak lanjut',
    description:
      'Diskusi dengan atasan untuk mengisi capaian, tantangan, dan upaya perbaikan.',
    editCtaText: process.env.NEXT_PUBLIC_SKP_EDIT_FORM_D_ENABLED === 'true' && (
      <>
        Ubah
        <span className="sr-only">Dokumen Refleksi Tindak Lanjut</span>
      </>
    ),
    lockedText: 'Menunggu nilai Tindak Lanjut dari atasan.',
    CtaButtonProps: {
      locked: {
        children: (
          <>
            Isi dokumen
            <span className="sr-only">Dokumen Refleksi Tindak Lanjut</span>
          </>
        ),
        color: 'blue',
        disabled: true,
      },
      unlocked: {
        children: (
          <>
            Isi dokumen
            <span className="sr-only">Dokumen Refleksi Tindak Lanjut</span>
          </>
        ),
        color: 'blue',
      },
      done: {
        children: (
          <>
            Cek rincian
            <span className="sr-only">Dokumen Refleksi Tindak Lanjut</span>
          </>
        ),
        color: 'white',
      },
      draft: {
        children: (
          <>
            Lanjutkan
            <span className="sr-only">Dokumen Refleksi Tindak Lanjut</span>
          </>
        ),
        color: 'blue',
      },
      event: {
        activityType: 'pelaksanaan_click_form_d',
        screenName: 'pelaksanaan_hub',
        attributes: {
          section: 'refleksi_tindak_lanjut_observasi',
        },
      },
    },
    url: `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/dokumen-refleksi-tindak-lanjut`,
    CardComponent: LangkahDokumenRefleksiTindakLanjutCard,
  },
]

export const tahapanPelaksanaan: TahapanPelaksanaanType = {
  bagian1,
  bagian2,
}
